import axios from "./_axios";
import { API } from "./apiEndPoints";
import configService from "../services/config";

const toAskIdString = (value) => {
  if (value == null || value === "") return null;
  return String(value);
};

export const mergeAskPayloadAccessContext = (payload) => {
  let account_id = null;
  let access_id = null;
  try {
    const accountRaw = localStorage.getItem("currentAccountID");
    if (accountRaw != null && accountRaw !== "") {
      account_id = toAskIdString(JSON.parse(accountRaw));
    }
  } catch {
    account_id = null;
  }
  try {
    const raw = localStorage.getItem("accessPermission");
    if (raw) {
      const ap = JSON.parse(raw);
      access_id = toAskIdString(ap?.id);
    }
  } catch {
    access_id = null;
  }
  return { ...payload, account_id, access_id };
};

const isDefaultAskRequest = (customEndpoint, customPayload) => !customEndpoint && !customPayload;

/**
 * Discrete status lines from `/video_to_components/` (and legacy heartbeat thoughts).
 * These arrive as complete sentences per SSE event — replace the banner text instead of
 * concatenating them into one glued string (IQThinkingBanner appends token streams).
 */
const IQ_STAGE_THOUGHT_PREFIXES = [
  "Preparing video for analysis",
  "Analyzing video with Gemini",
  "Finalizing video components",
  "Still preparing the video",
  "Still analyzing the video",
  "Still finalizing video components",
];

export const isIqStageThought = (text) => {
  const trimmed = String(text ?? "").trim();
  if (!trimmed) return false;
  return IQ_STAGE_THOUGHT_PREFIXES.some((prefix) => trimmed.startsWith(prefix));
};

/**
 * Accumulate streamed thinking for the banner.
 * Stage status thoughts replace the previous message; Gemini token chunks still append.
 */
export const accumulateThinkingText = (accumulatedThinking, text) => {
  if (isIqStageThought(text)) {
    return text;
  }
  const prior = accumulatedThinking || "";
  // Only break once: prior is still exactly a stage status (no reasoning body yet).
  if (prior && isIqStageThought(prior) && !prior.includes("\n")) {
    return `${prior}\n\n${text}`;
  }
  return `${prior}${text}`;
};

/**
 * Attach S3/object references to a default IQ ``/ask`` payload (IQ Chat Rule 12).
 * IQ expects each entry to carry the storage reference; the raw file is never
 * posted. A text-only question keeps the payload unchanged.
 */
const withAskAttachments = (payload, attachments) => {
  if (!Array.isArray(attachments) || attachments.length === 0) {
    return payload;
  }
  return { ...payload, attachments };
};

const withAskMentions = (payload, mentions) => {
  if (!Array.isArray(mentions) || mentions.length === 0) {
    return payload;
  }
  return { ...payload, mentions };
};

const unescapeText = (text) => {
  return text
    .replace(/\\n/g, "\n")
    .replace(/\\'/g, "'")
    .replace(/\\"/g, '"')
    .replace(/\\\\/g, "\\");
};

const containsMetadataMarkers = (text) => {
  return text.includes("'extras'") || text.includes("'signature'");
};

const isPythonDictFormat = (text) => {
  return text.includes("'type':") || text.includes('"type":');
};

const extractTextFromDict = (dictStr) => {
  const textMatch = /'text'\s*:\s*'((?:[^'\\]|\\.)*)'/.exec(dictStr);
  if (!textMatch) {
    return null;
  }
  const text = textMatch[1];
  if (text?.trim()) {
    return unescapeText(text);
  }
  return null;
};

const parseAnswerText = (rawAnswer) => {
  if (!rawAnswer || typeof rawAnswer !== "string") return rawAnswer;

  if (!isPythonDictFormat(rawAnswer)) {
    return rawAnswer;
  }

  try {
    const extractedTexts = [];
    const parts = rawAnswer.split(/\}\s*\{/);

    for (let i = 0; i < parts.length; i++) {
      let part = parts[i];
      if (i > 0) part = "{" + part;
      if (i < parts.length - 1) part = part + "}";

      if (containsMetadataMarkers(part)) {
        continue;
      }

      const text = extractTextFromDict(part);
      if (text) {
        extractedTexts.push(text);
      }
    }

    if (extractedTexts.length > 0) {
      return extractedTexts.join("");
    }
  } catch (e) {
    console.warn("Failed to parse answer text:", e);
  }

  return rawAnswer;
};

export const askIQ = async (params) => {
  const {
    question,
    k = 5,
    signal,
    customEndpoint,
    customPayload,
    conversationId,
    attachments,
    mentions,
  } = params;

  const iqBaseUrl = configService.get("iqBaseUrl");

  const axiosInstance = axios(true, {
    baseURL: customEndpoint ? "" : iqBaseUrl,
  });

  let payload = customPayload || { question, k };
  if (isDefaultAskRequest(customEndpoint, customPayload)) {
    payload = mergeAskPayloadAccessContext(payload);
    if (conversationId != null && conversationId !== "") {
      payload = {
        ...payload,
        conversation_id: toAskIdString(conversationId),
      };
    }
    payload = withAskMentions(withAskAttachments(payload, attachments), mentions);
  }

  const result = await axiosInstance({
    method: "post",
    url: customEndpoint || API.ASK_IQ,
    data: payload,
    signal,
  });

  return result;
};

/**
 * Rule 4 hand-off: upload a job-description document to the IQ microservice for
 * extraction. Returns a flat, ordered list of candidate responsibility bullets
 * plus the Rule 3a title-mismatch metadata.
 *
 * Shape: { bullets: string[], title_mismatch: boolean, position_title: string, jd_title: string }
 *
 * Depends on the extraction endpoint built in ER-11532.
 */
export const extractPositionImportResponsibilities = async ({ positionId, file, signal } = {}) => {
  const iqBaseUrl = configService.get("iqBaseUrl");
  const axiosInstance = axios(true, { baseURL: iqBaseUrl });

  const formData = new FormData();
  if (file != null) {
    formData.append("file", file);
  }
  if (positionId != null) {
    formData.append("position_id", toAskIdString(positionId));
  }

  const result = await axiosInstance({
    method: "post",
    url: API.POSITION_IMPORT_EXTRACT,
    data: formData,
    signal,
    headers: { "Content-Type": "multipart/form-data" },
  });

  return result.data;
};

const parseStreamingTextContent = (textContent) => {
  if (typeof textContent !== "string" || !isPythonDictFormat(textContent)) {
    return { text: textContent, skip: false };
  }

  if (containsMetadataMarkers(textContent)) {
    return { text: null, skip: true };
  }

  const extracted = extractTextFromDict(textContent);
  if (extracted === null) {
    return { text: null, skip: true };
  }

  return { text: extracted, skip: false };
};

const processTextEvent = (data, accumulatedThinking, onThinkingUpdate) => {
  // IQ microservice `thought` events use `data.text`; the Job Family suggest stream
  // (ER-12294) emits distilled progress lines under `data.message`. Accept both.
  const rawText =
    data && typeof data === "object"
      ? data.text !== undefined
        ? data.text
        : data.message
      : undefined;

  if (rawText === undefined) {
    return { accumulated: accumulatedThinking, skip: false };
  }

  const { text, skip } = parseStreamingTextContent(rawText);
  if (skip || text === null) {
    return { accumulated: accumulatedThinking, skip: true };
  }

  const newAccumulated = accumulateThinkingText(accumulatedThinking, text);
  if (onThinkingUpdate) {
    onThinkingUpdate(newAccumulated);
  }

  return { accumulated: newAccumulated, skip: false };
};

/**
 * Process a complete event from SSE stream (answer, suggestions, or video instructions)
 * @param {Object} data - The parsed JSON data
 * @param {Function} onComplete - Callback when complete
 * @returns {Object|null} The final response or null
 */
const processCompleteEvent = (data, onComplete, forceComplete = false) => {
  // Check for any of the expected complete response types:
  // - answer: regular Q&A response
  // - suggestions: checkpoint/responsibility suggestions
  // - instructions: video instructions response
  // - validation: video validation response (PASS or FAIL)
  // - levels: Job Family level suggestions (ER-12294) — `{ levels, changes, snapshot_id }`
  const isCompleteResponse =
    forceComplete ||
    data.answer !== undefined ||
    data.suggestions !== undefined ||
    data.instructions !== undefined ||
    data.validation !== undefined ||
    data.inspection_instructions !== undefined ||
    data.table_of_contents !== undefined ||
    data.quiz !== undefined ||
    data.questions !== undefined ||
    data.video_url !== undefined ||
    data.levels !== undefined;

  if (!isCompleteResponse) {
    return null;
  }

  const parsedAnswer = data.answer ? parseAnswerText(data.answer) : null;
  const finalResponse = { ...data, answer: parsedAnswer };

  if (onComplete) {
    onComplete(finalResponse);
  }

  return finalResponse;
};

const processSSELine = (line, state, callbacks) => {
  const trimmedLine = line.trim();

  if (!trimmedLine) {
    return state;
  }

  if (trimmedLine.startsWith("event:")) {
    return {
      ...state,
      currentEvent: trimmedLine.slice(6).trim().toLowerCase(),
    };
  }

  if (!trimmedLine.startsWith("data:")) {
    return state;
  }

  const dataStr = trimmedLine.slice(5).trim();
  if (!dataStr) {
    return state;
  }

  try {
    const data = JSON.parse(dataStr);
    const eventType = state.currentEvent || "";
    const newState = { ...state, currentEvent: "" };

    if (eventType === "complete" || eventType === "result") {
      const completeResult = processCompleteEvent(data, callbacks.onComplete, true);
      if (completeResult) {
        newState.finalResponse = completeResult;
      }
      return newState;
    }

    if (eventType === "error") {
      const message =
        typeof data.error === "string" && data.error.trim() ? data.error.trim() : "Request failed";
      newState.streamError = message;
      // Keep the terminal frame so callers can map a machine-readable `reason`
      // (e.g. Pay Range suggest: no_sources / timeout / unavailable).
      newState.streamErrorData = data && typeof data === "object" ? data : null;
      if (callbacks.onError) {
        callbacks.onError(message);
      }
      return newState;
    }

    // Streamed AI Thinking Standard (ER-12217 / ER-12294): a distilled phase label.
    if (eventType === "phase") {
      const phase =
        typeof data === "string"
          ? data
          : (data && (data.phase || data.message || data.label)) || "";
      if (callbacks.onPhase && phase) {
        callbacks.onPhase(String(phase));
      }
      return newState;
    }

    // Silence watchdog: the stream is still open but no events have arrived recently.
    if (eventType === "stalled") {
      if (callbacks.onStalled) {
        callbacks.onStalled();
      }
      return newState;
    }

    if (eventType === "thought" || eventType === "thinking") {
      const textResult = processTextEvent(
        data,
        state.accumulatedThinking,
        callbacks.onThinkingUpdate
      );
      newState.accumulatedThinking = textResult.accumulated;
      return newState;
    }

    // Structured response body (quiz questions, inspection steps, etc.) — not thinking.
    if (eventType === "text") {
      return newState;
    }

    // Backward compatibility: infer behavior when event field is absent.
    const textResult = processTextEvent(
      data,
      state.accumulatedThinking,
      callbacks.onThinkingUpdate
    );
    newState.accumulatedThinking = textResult.accumulated;

    const completeResult = processCompleteEvent(data, callbacks.onComplete);
    if (completeResult) {
      newState.finalResponse = completeResult;
    }

    return newState;
  } catch (parseError) {
    console.warn("Failed to parse SSE data:", dataStr, parseError);
    return state;
  }
};

export const processSSEStream = async (reader, callbacks) => {
  const decoder = new TextDecoder();
  let state = {
    accumulatedThinking: "",
    finalResponse: null,
    buffer: "",
    currentEvent: "",
    streamError: null,
    streamErrorData: null,
  };

  try {
    let done = false;
    while (!done) {
      const result = await reader.read();
      done = result.done;

      if (done) break;

      state.buffer += decoder.decode(result.value, { stream: true });
      const lines = state.buffer.split("\n");
      state.buffer = lines.pop() || "";

      for (const line of lines) {
        state = processSSELine(line, state, callbacks);
      }
    }
  } finally {
    reader.releaseLock();
  }

  return {
    finalResponse: state.finalResponse,
    accumulatedThinking: state.accumulatedThinking,
    streamError: state.streamError || null,
    streamErrorData: state.streamErrorData || null,
  };
};

export const buildFetchConfig = (url, payload, signal, accept) => {
  const token = localStorage.getItem("token");
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(accept ? { Accept: accept } : {}),
      ...(token ? { Authorization: "Token " + token } : {}),
    },
    body: JSON.stringify(payload),
    signal,
  };
};

/**
 * Wrap the caller's abort signal with a max-wait timeout so a stalled SSE stream can't
 * hang forever. When timeoutMs is not provided the caller's signal is used unchanged,
 * which keeps existing consumers (e.g. the IQ chat overlay) untouched.
 */
const buildStreamTimeoutSignal = (callerSignal, timeoutMs) => {
  if (!timeoutMs || timeoutMs <= 0) {
    return { signal: callerSignal, isTimedOut: () => false, cleanup: () => {} };
  }

  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  const onCallerAbort = () => {
    clearTimeout(timer);
    controller.abort();
  };

  if (callerSignal) {
    if (callerSignal.aborted) {
      clearTimeout(timer);
      controller.abort();
    } else {
      callerSignal.addEventListener("abort", onCallerAbort, { once: true });
    }
  }

  return {
    signal: controller.signal,
    isTimedOut: () => timedOut,
    cleanup: () => {
      clearTimeout(timer);
      if (callerSignal) callerSignal.removeEventListener("abort", onCallerAbort);
    },
  };
};

/**
 * `streamJsonPost` uses the native fetch API, which does NOT honour axios's
 * `defaults.baseURL`. A relative path such as `api/common/v2/account/...` would
 * therefore resolve against the SPA origin (e.g.
 * `http://localhost:3000/organization/api/...`) instead of the API host.
 * Resolve relative URLs against the configured baseURL before fetching.
 */
const resolveStreamRequestUrl = (url) => {
  const raw = typeof url === "string" ? url.trim() : "";
  if (!raw || /^https?:\/\//i.test(raw) || raw.startsWith("//")) {
    return raw;
  }
  const baseURL = String(configService.get("baseURL") || "").replace(/\/+$/, "");
  if (!baseURL) {
    return raw;
  }
  return `${baseURL}/${raw.replace(/^\/+/, "")}`;
};

/**
 * POST JSON to a URL and consume an IQ-style SSE response (thought / complete / error).
 * Used for Django ask-ai-conversations messages and IQ /ask streaming.
 */
export const streamJsonPost = async ({
  url,
  payload,
  signal,
  timeoutMs,
  onThinkingUpdate,
  onComplete,
  onError,
}) => {
  const streamTimeout = buildStreamTimeoutSignal(signal, timeoutMs);
  const requestUrl = resolveStreamRequestUrl(url);

  try {
    const response = await fetch(
      requestUrl,
      buildFetchConfig(requestUrl, payload, streamTimeout.signal)
    );

    if (!response.ok) {
      let responseData;
      try {
        responseData = await response.json();
      } catch {
        responseData = null;
      }
      const httpError = new Error(`HTTP error! status: ${response.status}`);
      httpError.status = response.status;
      httpError.response = { status: response.status, data: responseData };
      throw httpError;
    }

    const reader = response.body.getReader();
    const { finalResponse, accumulatedThinking, streamError } = await processSSEStream(reader, {
      onThinkingUpdate,
      onComplete,
      onError,
    });

    if (streamError) {
      throw new Error(streamError);
    }

    return {
      data: finalResponse || {
        answer: accumulatedThinking,
        reasoning: accumulatedThinking,
      },
    };
  } catch (err) {
    if (streamTimeout.isTimedOut()) {
      const timeoutError = new Error("IQ generation timed out. Please try again.");
      timeoutError.name = "TimeoutError";
      throw timeoutError;
    }
    throw err;
  } finally {
    streamTimeout.cleanup();
  }
};

export const askIQStream = async (params) => {
  const {
    question,
    k = 5,
    signal,
    timeoutMs,
    customEndpoint,
    customPayload,
    conversationId,
    attachments,
    mentions,
    onThinkingUpdate,
    onComplete,
    onError,
  } = params;

  const iqBaseUrl = configService.get("iqBaseUrl");
  const url = customEndpoint || `${iqBaseUrl.replace(/\/$/, "")}/${API.ASK_IQ}`;
  let payload = customPayload || { question, k };
  if (isDefaultAskRequest(customEndpoint, customPayload)) {
    payload = mergeAskPayloadAccessContext(payload);
    if (conversationId != null && conversationId !== "") {
      payload = {
        ...payload,
        conversation_id: toAskIdString(conversationId),
      };
    }
    payload = withAskMentions(withAskAttachments(payload, attachments), mentions);
  }

  return streamJsonPost({
    url,
    payload,
    signal,
    timeoutMs,
    onThinkingUpdate,
    onComplete,
    onError,
  });
};

const ensureHttpsIqBaseUrl = (baseUrl) => {
  let u = (baseUrl || "").trim().replace(/\/$/, "");
  if (!u) return u;
  if (/^https:\/\//i.test(u)) return u;
  if (/^http:\/\//i.test(u)) return u.replace(/^http:\/\//i, "https://");
  if (u.startsWith("//")) return `https:${u}`;
  if (!/^https?:\/\//i.test(u)) return `https://${u}`;
  return u;
};

const buildHttpsIqConversationsUrl = (accessPermissionId, accountId) => {
  const raw = (configService.get("iqBaseUrl") || "").trim();
  const normalized = ensureHttpsIqBaseUrl(raw).replace(/\/$/, "");
  const hrefSeed = normalized || "https://iq.dev.exiqtive.com";
  const href = /^https?:\/\//i.test(hrefSeed) ? hrefSeed : `https://${hrefSeed}`;
  const u = new URL(href);
  u.protocol = "https:";
  const id = encodeURIComponent(String(accessPermissionId));
  u.pathname = `/conversations/${id}`;
  if (accountId != null && accountId !== "") {
    u.searchParams.set("account_id", String(accountId));
  }
  return u.toString();
};

export const fetchIQConversations = async (accessPermissionId, accountId) => {
  const url = buildHttpsIqConversationsUrl(accessPermissionId, accountId);
  const axiosInstance = axios(true, { baseURL: "" });
  return axiosInstance({
    method: "get",
    url,
  });
};

export const fetchIQConversationDetail = async (accessPermissionId, conversationId, accountId) => {
  const raw = (configService.get("iqBaseUrl") || "").trim();
  const normalized = ensureHttpsIqBaseUrl(raw).replace(/\/$/, "");
  const hrefSeed = normalized || "https://iq.dev.exiqtive.com";
  const href = /^https?:\/\//i.test(hrefSeed) ? hrefSeed : `https://${hrefSeed}`;
  const u = new URL(href);
  u.protocol = "https:";
  const a = encodeURIComponent(String(accessPermissionId));
  const c = encodeURIComponent(String(conversationId));
  u.pathname = `/conversations/${a}/${c}`;
  if (accountId != null && accountId !== "") {
    u.searchParams.set("account_id", String(accountId));
  }
  const axiosInstance = axios(true, { baseURL: "" });
  return axiosInstance({
    method: "get",
    url: u.toString(),
  });
};
